"use client";

import { useState, useRef, useEffect } from "react";
import styles from "./LpFaq.module.css";

const FAQ_ITEMS = [
    {
        question: "O que é a Flexpro?",
        answer: "A Flexpro é uma empresa de tecnologia especializada no mercado imobiliário, com mais de 20 anos de atuação e soluções construídas a partir da vivência real da operação de uma imobiliária.\n\nDesde a sua origem, a Flexpro não foi pensada como um software genérico, mas como uma forma de organizar a complexidade do dia a dia imobiliário. Por isso, desenvolvemos um ecossistema de soluções que conecta gestão operacional, comercial, atendimento e aplicativos, sempre com foco em clareza, integração e previsibilidade.\n\nMais do que tecnologia, a Flexpro entende o negócio imobiliário. Entende processos, recorrência, volume operacional e os desafios envolvidos em crescer sem perder controle. Cada solução é construída para se adaptar à realidade de cada imobiliária, respeitando seu fluxo, suas regras e sua forma de operar.\n\nNosso papel é apoiar imobiliárias a tomarem decisões mais seguras, reduzirem retrabalho e sustentarem o crescimento com organização, usando tecnologia como meio e não como fim."
    },
    {
        question: "O sistema é indicado para imobiliárias de pequeno, médio ou grande porte?",
        answer: "O sistema foi idealizado para atender imobiliárias de todos os portes. Sua arquitetura flexível permite que uma imobiliária em fase de crescimento utilize as principais ferramentas, enquanto imobiliárias maiores usufruem do ecossistema completo para sustentar altos volumes operacionais com a mesma eficiência."
    },
    {
        question: "O sistema atende locação, vendas e financeiro no mesmo lugar?",
        answer: "Sim, o SIMOB unifica Locação, Vendas e Financeiro em um único ambiente, eliminando a fragmentação de dados e reduzindo a necessidade de planilhas paralelas ou de múltiplos sistemas que não se comunicam."
    },
    {
        question: "Dá para controlar toda a operação da imobiliária em um único sistema?",
        answer: "Com o nosso ecossistema integrado (SIMOB para gestão de locação/vendas/financeiro, MOOV para o funil comercial e Aplicativos), a sua operação funciona de ponta a ponta sem perder o fio condutor dos processos."
    },
    {
        question: "O sistema ajuda a reduzir retrabalho na imobiliária?",
        answer: "Exatamente. Automações na emissão de contratos, integração bancária, conciliação e repasses ao proprietário são estruturadas para evitar dupla digitação, mitigando falhas humanas na rotina diária."
    },
    {
        question: "Consigo organizar a rotina da equipe com esse sistema?",
        answer: "Sim. Nossos relatórios, painéis (dashboards) e indicadores gerenciais dão à liderança visão total das tarefas em andamento, responsáveis por cada demanda e gargalos processuais na esteira da imobiliária."
    },
    {
        question: "O sistema se adapta ao fluxo da minha imobiliária ou preciso me adaptar a ele?",
        answer: "O software é construído em cima de parâmetros ajustáveis. Ele respeita a forma como você opera hoje, ao mesmo tempo em que fornece os trilhos das melhores práticas do mercado imobiliário para te impulsionar a crescer com segurança."
    },
    {
        question: "Dá para padronizar processos sem engessar a operação?",
        answer: "A Flexpro permite que você crie regras de negócio bem definidas (como limites, alçadas de aprovação e contratos padronizados) para garantir segurança, deixando a equipe livre para focar no atendimento estratégico."
    }
];

interface FAQItemProps {
    question: string;
    answer: string;
    isOpen: boolean;
    onClick: () => void;
}

function FAQItem({ question, answer, isOpen, onClick }: FAQItemProps) {
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        if (isOpen && contentRef.current) {
            setHeight(contentRef.current.scrollHeight);
        } else {
            setHeight(0);
        }
    }, [isOpen]);

    return (
        <div className={`${styles.item} ${isOpen ? styles.open : ""}`}>
            <button className={styles.questionButton} onClick={onClick}>
                <span className={styles.questionText}>{question}</span>
                <div className={styles.icon}></div>
            </button>
            <div
                className={styles.answerWrapper}
                style={{ maxHeight: `${height}px` }}
            >
                <div
                    ref={contentRef}
                    className={styles.answerContent}
                    dangerouslySetInnerHTML={{ __html: answer.replace(/\n\n/g, '<br/><br/>') }}
                />
            </div>
        </div>
    );
}

export default function LpFaq() {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const handleToggle = (index: number) => {
        setOpenIndex(prevIndex => (prevIndex === index ? null : index));
    };

    return (
        <section className={styles.section} id="faq">
            <div className={styles.container}>
                <div className={styles.faqList}>
                    {FAQ_ITEMS.map((item, index) => (
                        <FAQItem
                            key={index}
                            question={item.question}
                            answer={item.answer}
                            isOpen={openIndex === index}
                            onClick={() => handleToggle(index)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
